import type { GameState, Passenger } from '../types.js';
import { carCarrying } from './cars.js';
import { chargeFailedDelivery, chargeGaveUp } from './service.js';

/**
 * The two ways a passenger is lost.
 *
 * Both are shown as a draining bar, and the rule behind them is the same
 * promise: when the bar empties, that person is gone. A bar that runs out and
 * does nothing is worse than no bar at all — it teaches the player that none of
 * them mean anything, which quietly disarms every other clock on screen.
 *
 * What differs is the calculation each one asks for:
 *
 *   patience — starts when they appear, runs only while they wait.
 *              "Can I reach them in time?"
 *   deadline — an absolute time for the delivery, and it keeps running after
 *              they board. "Can I reach them AND get them there in time?"
 *
 * That second clause is why a courier can be lost from inside the cabin. It is
 * harsh, but it is the whole trait: taking a delivery you cannot complete is
 * supposed to be a mistake you can see coming.
 */
export function applyAttrition(state: GameState): void {
  for (const passenger of state.passengers) {
    if (passenger.state === 'waiting' && outOfPatience(state, passenger)) {
      passenger.state = 'abandoned';
      passenger.abandonedMs = state.elapsedMs;
      passenger.lostReason = 'gave-up';
      chargeGaveUp(state, passenger);
      continue;
    }

    const riding = passenger.state === 'riding';
    if ((passenger.state === 'waiting' || riding) && pastDeadline(state, passenger)) {
      const car = riding ? carCarrying(state, passenger.id) : null;
      if (car) {
        car.riders.splice(car.riders.indexOf(passenger.id), 1);
        car.load -= passenger.size;
      }
      passenger.state = 'abandoned';
      passenger.abandonedMs = state.elapsedMs;
      passenger.lostReason = 'deadline';
      chargeFailedDelivery(state, passenger);
    }
  }
}

function outOfPatience(state: GameState, passenger: Passenger): boolean {
  if (passenger.patienceMs === null) return false;
  // From the start of the *current* wait. Identical to spawn time in a building
  // with no interchange; the difference is that somebody who has just been
  // carried halfway does not give up the moment they step out.
  return state.elapsedMs - passenger.waitingSinceMs >= passenger.patienceMs;
}

function pastDeadline(state: GameState, passenger: Passenger): boolean {
  return passenger.deadlineMs !== null && state.elapsedMs >= passenger.deadlineMs;
}

/** Milliseconds left before this passenger gives up, or null if they never will. */
export function patienceRemaining(state: GameState, passenger: Passenger): number | null {
  if (passenger.patienceMs === null || passenger.state !== 'waiting') return null;
  return Math.max(0, passenger.patienceMs - (state.elapsedMs - passenger.waitingSinceMs));
}

/** Milliseconds left on a delivery deadline, or null if there is none. */
export function deadlineRemaining(state: GameState, passenger: Passenger): number | null {
  if (passenger.deadlineMs === null) return null;
  if (passenger.state !== 'waiting' && passenger.state !== 'riding') return null;
  return Math.max(0, passenger.deadlineMs - state.elapsedMs);
}

/**
 * How much of a deadline is left, from 1 down to 0, or null if there is none.
 *
 * A courier's runs from the start of the level. A crew's runs from their own
 * arrival (ADR-0029), and the return they bring back runs on the same clock —
 * so the full span is the crew's `withinMs`, not the absolute deadline.
 */
export function deadlineFraction(state: GameState, passenger: Passenger): number | null {
  const left = deadlineRemaining(state, passenger);
  if (left === null || passenger.deadlineMs === null) return null;
  const leader =
    passenger.follows === null ? null : state.passengers.find((p) => p.id === passenger.follows);
  const span = passenger.withinMs ?? leader?.withinMs ?? passenger.deadlineMs;
  return span > 0 ? Math.min(1, left / span) : 0;
}
